import type { CanvasLocalFilePreview } from "../context/CanvasLocalFilePreviewContext";

export type FileRecordDisplayFields = {
  id?: string;
  title?: string;
  status?: string;
  url?: string;
  content_type?: string;
  kind?: string;
  preview_urls?: Record<string, string> | null;
};

export type MergeLocalPreviewOptions = {
  /** When false, keep the blob visible for video uploads even after `processed`. */
  videoHandoffReady?: boolean;
};

export function fileRecordIsProcessed(status: string | undefined): boolean {
  return status === "processed";
}

export function fileRecordHasDisplayableContent(
  data: FileRecordDisplayFields | null | undefined,
): boolean {
  if (!data) return false;
  if (data.url && data.url.length > 0) return true;
  if (!data.preview_urls) return false;
  return Object.values(data.preview_urls).some((u) => u.length > 0);
}

function shouldKeepLocalPreview(
  data: FileRecordDisplayFields,
  localPreview: CanvasLocalFilePreview,
  options?: MergeLocalPreviewOptions,
): boolean {
  if (!fileRecordIsProcessed(data.status)) return true;
  if (!localPreview.contentType.startsWith("video/")) return false;
  return options?.videoHandoffReady !== true;
}

export function mergeFileRecordWithLocalPreview(
  data: FileRecordDisplayFields | null | undefined,
  localPreview: CanvasLocalFilePreview | undefined,
  options?: MergeLocalPreviewOptions,
): FileRecordDisplayFields | null | undefined {
  if (!data || !localPreview) return data;
  if (shouldKeepLocalPreview(data, localPreview, options)) {
    return {
      ...data,
      url: localPreview.url,
      content_type: localPreview.contentType,
    };
  }
  if (fileRecordHasDisplayableContent(data)) return data;
  return {
    ...data,
    url: localPreview.url,
    content_type: localPreview.contentType,
  };
}

/** Only drop the in-memory blob once the server row is `processed` with media. */
export function shouldRevokeLocalFilePreview(
  data: FileRecordDisplayFields | null | undefined,
  localPreview?: CanvasLocalFilePreview,
): boolean {
  if (!data || !fileRecordIsProcessed(data.status)) return false;
  if (localPreview?.contentType.startsWith("video/")) return false;
  return fileRecordHasDisplayableContent(data);
}

export function shouldShowFileRecordSkeleton(params: {
  loading?: boolean;
  isFileRecordEntity: boolean;
  fileRecordData: FileRecordDisplayFields | null | undefined;
  localPreview: CanvasLocalFilePreview | undefined;
  videoHandoffReady?: boolean;
}): boolean {
  const { loading, isFileRecordEntity, fileRecordData, localPreview } = params;
  if (!isFileRecordEntity && !loading) return false;

  const displayData = mergeFileRecordWithLocalPreview(
    fileRecordData,
    localPreview,
    { videoHandoffReady: params.videoHandoffReady },
  );
  if (fileRecordHasDisplayableContent(displayData)) return false;

  return loading === true || isFileRecordEntity;
}

/**
 * The node references a file that can no longer be read.
 *
 * Two wire shapes say it. A node holding the file's own id gets a 404 once the
 * file is archived (`hydrationGone`). A node holding a reference row's id
 * gets a 200 carrying the server's deliberate placeholder: the link row alone,
 * with no url and no previews. Broken and no-access are one state on purpose —
 * distinguishing them would report whether a file you cannot see exists.
 *
 * A link whose target is still uploading also has no url yet; that is a
 * skeleton, not a dead reference.
 */
export function fileRecordIsUnavailable(params: {
  /**
   * Hydration came back definitively gone (404/403) — NOT any hydration
   * error. A 500, a timeout or an offline tab must leave the node in its
   * loading state or on its last good card; painting those as a dead
   * reference tells the user their file was destroyed by a blip.
   */
  hydrationGone: boolean;
  /**
   * The API answered for this record in this session. The placeholder check
   * below reads a *missing* url as the server's verdict, so it may only run
   * against the server's own answer: a freshly minted reference's Loro stub,
   * a fetch still in flight and a restored cache row stripped of its expired
   * presigns all look identical to a dead link, and painting them Unavailable
   * flashes it on every import and every reload.
   */
  hydrationSettled: boolean;
  fileRecordData: FileRecordDisplayFields | null | undefined;
}): boolean {
  if (params.hydrationGone) return true;
  if (!params.hydrationSettled) return false;
  const data = params.fileRecordData;
  if (!data || data.kind !== "link") return false;
  if (data.status === "pending") return false;
  return !fileRecordHasDisplayableContent(data);
}
